import { NavLink, useLocation } from 'react-router-dom';
import {
  Activity,
  Contact,
  FolderOpen,
  Inbox,
  LayoutDashboard,
  Lock,
  type LucideIcon,
  Mail,
  Package,
  Receipt,
  Scale,
  ShieldCheck,
  User,
  Users,
  Wallet,
} from 'lucide-react';
import { Icon } from './ui';
import { NAV_SECTIONS, findActiveNav } from './navConfig';
import { canSeeNavItem } from './navPerms';

// Per-category sidebar icon, keyed by section label. Lives here (not in the
// pure, test-only navConfig) so navConfig stays free of React/lucide imports.
const SECTION_ICON: Record<string, LucideIcon> = {
  Overview: LayoutDashboard,
  'My Workspace': User,
  Webmail: Mail,
  Operations: Activity,
  Staff: Users,
  Directory: Contact,
  'Executive Protection': ShieldCheck,
  Assets: Package,
  Billing: Receipt,
  Financials: Wallet,
  Compliance: Scale,
  Admin: FolderOpen,
  'DSR Admin': Lock,
  Applicant: Inbox,
};

// Sidebar shows one row per category (the section's subheadings now live in the
// horizontal SectionTabs bar). Each category links to its first permitted item;
// the active category is computed from the current route (categories have no
// route of their own, so NavLink's isActive can't drive it).
export function NavMenu(): JSX.Element {
  const { pathname } = useLocation();

  // Drop any item the user lacks permission for, then drop whole sections
  // that end up empty.
  const visibleSections = NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter(canSeeNavItem),
  })).filter((section) => section.items.length > 0);

  const active = findActiveNav(pathname, visibleSections);

  return (
    <nav className="nav-menu" aria-label="Primary">
      <ul className="nav-cat-list">
        {visibleSections.map((section) => {
          // visibleSections drops empty sections, so items[0] always exists.
          const firstItem = section.items[0];
          if (!firstItem) return null;
          const isActive = active?.section.label === section.label;
          const LucideGlyph = SECTION_ICON[section.label];
          const fallbackGlyph = section.icon ?? firstItem.icon;
          return (
            <li key={section.label}>
              <NavLink
                to={firstItem.to}
                className={`nav-cat ${isActive ? 'active' : ''}`}
                aria-current={isActive ? 'page' : undefined}
              >
                <span className="nav-cat-icon" aria-hidden="true">
                  {LucideGlyph ? <Icon icon={LucideGlyph} size={17} /> : fallbackGlyph}
                </span>
                <span className="nav-cat-label">{section.label}</span>
              </NavLink>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
